export interface PaymentProcessorPermission {
  label: string;
  enabled: boolean;
}

export interface PaymentProcessorUsageFunction {
  id: string;
  label: string;
  enabled: boolean;
}

export interface AnnualLicenseConfig {
  enabled: boolean;
  amount: number;
  periodicity: 'annual';
  description: string;
  includes: string[];
  stats: {
    payingRadios: number;
    expiringThisMonth: number;
    projectedRevenue: number;
  };
}

export interface PaymentProcessorConfigField {
  key: string;
  label: string;
  value: string;
  type: 'text' | 'password' | 'textarea';
  placeholder?: string;
  help?: string;
}

export type ProcessorCategory = 'api' | 'manual';
export type WebhookStatus = 'healthy' | 'failing' | 'unconfigured';
export type ProcessorStatus = 'active' | 'inactive' | 'error' | 'disconnected';

export interface PaymentProcessor {
  id: string;
  name: string;
  icon: string;
  logoColor: string;
  category: ProcessorCategory;
  status: ProcessorStatus;
  enabled: boolean;
  isDefault: boolean;
  testMode: boolean;
  order: number;
  lastSync: string | null;
  lastTest: string | null;
  webhookStatus: WebhookStatus | null;
  commissionPercent: number;
  commissionFixed: number;
  descriptor: string;
  config: PaymentProcessorConfigField[];
  manualInstructions?: string;
  receiverName?: string;
  receiverContact?: string;
  permissions: PaymentProcessorPermission[];
  usageFunctions: PaymentProcessorUsageFunction[];
}

export const processorStatusLabels: Record<ProcessorStatus, string> = {
  active: 'Conectado',
  inactive: 'Inactivo',
  error: 'Error',
  disconnected: 'Desconectado',
};

export const processorStatusColors: Record<ProcessorStatus, string> = {
  active: 'bg-emerald-50 text-emerald-600',
  inactive: 'bg-background-200 text-foreground-400',
  error: 'bg-red-50 text-red-500',
  disconnected: 'bg-foreground-100 text-foreground-500',
};

export const webhookStatusLabels: Record<WebhookStatus, string> = {
  healthy: 'Saludable',
  failing: 'Fallando',
  unconfigured: 'Sin configurar',
};

export const webhookStatusIcons: Record<WebhookStatus, string> = {
  healthy: 'ri-check-line',
  failing: 'ri-error-warning-line',
  unconfigured: 'ri-link-unlink',
};

export const webhookStatusColors: Record<WebhookStatus, string> = {
  healthy: 'text-emerald-500',
  failing: 'text-red-400',
  unconfigured: 'text-foreground-300',
};

export const paymentProcessors: PaymentProcessor[] = [
  {
    id: 'stripe',
    name: 'Stripe',
    icon: 'ri-bank-card-line',
    logoColor: 'bg-violet-50 text-violet-600',
    category: 'api',
    status: 'active',
    enabled: true,
    isDefault: true,
    testMode: false,
    order: 1,
    lastSync: '2026-06-10 09:42',
    lastTest: '2026-06-10 09:41',
    webhookStatus: 'healthy',
    commissionPercent: 2.9,
    commissionFixed: 0.30,
    descriptor: 'RFW*',
    config: [
      { key: 'publishable_key', label: 'Publishable Key', value: 'pk_live_x8k2mNpQrL7wYvT3aBcD4eF', type: 'text', placeholder: 'pk_live_...', help: 'Clave pública de Stripe para el frontend' },
      { key: 'secret_key', label: 'Secret Key', value: 'sk_live_Jh9kWqPzR6tYxN2vB8mC3dF', type: 'password', placeholder: 'sk_live_...', help: 'Clave secreta — nunca se expone al frontend' },
      { key: 'webhook_url', label: 'Webhook URL', value: 'https://api.radiofrecuenciaweb.com/webhooks/stripe', type: 'text', placeholder: 'https://...', help: 'URL que Stripe llama ante eventos de pago' },
    ],
    permissions: [
      { label: 'Procesar pagos con tarjeta', enabled: true },
      { label: 'Procesar reembolsos', enabled: true },
      { label: 'Gestionar suscripciones', enabled: true },
      { label: 'Apple Pay / Google Pay', enabled: true },
      { label: 'Soporte multidivisa (135+ monedas)', enabled: true },
      { label: 'Fraud detection (Radar)', enabled: false },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: true },
      { id: 'renewal', label: 'Renovaciones', enabled: true },
      { id: 'annual_license', label: 'Licencia anual', enabled: true },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: true },
      { id: 'premium_domain', label: 'Dominio premium', enabled: false },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: true },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
  {
    id: 'paypal',
    name: 'PayPal',
    icon: 'ri-paypal-line',
    logoColor: 'bg-sky-50 text-sky-600',
    category: 'api',
    status: 'active',
    enabled: true,
    isDefault: false,
    testMode: false,
    order: 2,
    lastSync: '2026-06-10 08:15',
    lastTest: '2026-06-09 16:30',
    webhookStatus: 'healthy',
    commissionPercent: 3.5,
    commissionFixed: 0.49,
    descriptor: 'RADIOWEB',
    config: [
      { key: 'client_id', label: 'Client ID', value: 'AcXyZ1234567890AbCdEfGhIjKlMnOp', type: 'text', placeholder: 'Client ID de PayPal...', help: 'ID de aplicación PayPal' },
      { key: 'secret', label: 'Secret', value: 'EL_mYpAyPaLsEcReT_x8k2mNpQrL7w', type: 'password', placeholder: 'Secret de PayPal...', help: 'Clave secreta de la app PayPal' },
      { key: 'webhook_url', label: 'Webhook URL', value: 'https://api.radiofrecuenciaweb.com/webhooks/paypal', type: 'text', placeholder: 'https://...', help: 'URL de notificaciones instantáneas (IPN)' },
    ],
    permissions: [
      { label: 'Procesar pagos con PayPal', enabled: true },
      { label: 'Procesar reembolsos', enabled: true },
      { label: 'PayPal Credit', enabled: false },
      { label: 'Soporte multidivisa', enabled: true },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: false },
      { id: 'renewal', label: 'Renovaciones', enabled: true },
      { id: 'annual_license', label: 'Licencia anual', enabled: true },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: false },
      { id: 'premium_domain', label: 'Dominio premium', enabled: false },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: true },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
  {
    id: 'square',
    name: 'Square',
    icon: 'ri-smartphone-line',
    logoColor: 'bg-teal-50 text-teal-600',
    category: 'api',
    status: 'disconnected',
    enabled: false,
    isDefault: false,
    testMode: true,
    order: 3,
    lastSync: null,
    lastTest: null,
    webhookStatus: 'unconfigured',
    commissionPercent: 2.6,
    commissionFixed: 0.30,
    descriptor: '',
    config: [
      { key: 'application_id', label: 'Application ID', value: '', type: 'text', placeholder: 'sq0idp-...', help: 'ID de aplicación en Square Developer' },
      { key: 'access_token', label: 'Access Token', value: '', type: 'password', placeholder: 'Access Token de Square...', help: 'Token de acceso OAuth de Square' },
      { key: 'location_id', label: 'Location ID', value: '', type: 'text', placeholder: 'ID de ubicación...', help: 'ID de la ubicación del negocio en Square' },
    ],
    permissions: [
      { label: 'Procesar pagos con Square', enabled: true },
      { label: 'Pagos presenciales (POS)', enabled: true },
      { label: 'Facturación Square', enabled: true },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: false },
      { id: 'renewal', label: 'Renovaciones', enabled: false },
      { id: 'annual_license', label: 'Licencia anual', enabled: false },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: false },
      { id: 'premium_domain', label: 'Dominio premium', enabled: false },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: false },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
  {
    id: 'zelle',
    name: 'Zelle',
    icon: 'ri-smartphone-line',
    logoColor: 'bg-indigo-50 text-indigo-600',
    category: 'manual',
    status: 'active',
    enabled: true,
    isDefault: false,
    testMode: false,
    order: 4,
    lastSync: null,
    lastTest: '2026-05-28 11:00',
    webhookStatus: null,
    commissionPercent: 0,
    commissionFixed: 0,
    descriptor: '',
    receiverName: 'RadioFrecuenciaWeb LLC',
    receiverContact: 'billing@radiofrecuenciaweb.com',
    manualInstructions: '1. Abrí tu app bancaria o Zelle.\n2. Seleccioná enviar dinero.\n3. Ingresá el email billing@radiofrecuenciaweb.com como destinatario.\n4. Poné el número de factura en el memo.\n5. Envianos el comprobante a soporte@radiofrecuenciaweb.com.',
    config: [
      { key: 'receiver_name', label: 'Nombre del receptor', value: 'RadioFrecuenciaWeb LLC', type: 'text', placeholder: 'Nombre o razón social...' },
      { key: 'receiver_email', label: 'Email / Teléfono', value: 'billing@radiofrecuenciaweb.com', type: 'text', placeholder: 'Email o teléfono Zelle...' },
      { key: 'instructions', label: 'Instrucciones', value: 'Enviar comprobante a soporte@radiofrecuenciaweb.com con número de factura.', type: 'textarea', placeholder: 'Instrucciones para el pagador...' },
    ],
    permissions: [
      { label: 'Pagos manuales vía Zelle', enabled: true },
      { label: 'Confirmación manual por admin', enabled: true },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: false },
      { id: 'renewal', label: 'Renovaciones', enabled: false },
      { id: 'annual_license', label: 'Licencia anual', enabled: false },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: true },
      { id: 'premium_domain', label: 'Dominio premium', enabled: true },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: false },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
  {
    id: 'bank_transfer',
    name: 'Transferencia Bancaria',
    icon: 'ri-bank-line',
    logoColor: 'bg-emerald-50 text-emerald-600',
    category: 'manual',
    status: 'active',
    enabled: true,
    isDefault: false,
    testMode: false,
    order: 5,
    lastSync: null,
    lastTest: null,
    webhookStatus: null,
    commissionPercent: 0,
    commissionFixed: 0,
    descriptor: '',
    receiverName: 'RadioFrecuenciaWeb S.A.',
    receiverContact: 'Cuenta Corriente #1234-567890/1',
    manualInstructions: '1. Realizá la transferencia a la cuenta indicada.\n2. Usá el número de factura como referencia.\n3. Envianos el comprobante a cobranzas@radiofrecuenciaweb.com.\n4. La acreditación demora entre 24 y 48 horas hábiles.',
    config: [
      { key: 'bank_name', label: 'Banco', value: 'Banco Nación', type: 'text', placeholder: 'Nombre del banco...' },
      { key: 'account_number', label: 'Número de cuenta', value: '1234-567890/1', type: 'text', placeholder: 'CBU / CVU / Nro de cuenta...' },
      { key: 'routing', label: 'Código de routing', value: '011000138', type: 'text', placeholder: 'ABA Routing Number...' },
      { key: 'swift', label: 'Código SWIFT', value: 'NACNARBAXXX', type: 'text', placeholder: 'SWIFT / BIC...' },
      { key: 'instructions', label: 'Instrucciones', value: 'Usar número de factura como referencia. Enviar comprobante a cobranzas@radiofrecuenciaweb.com.', type: 'textarea', placeholder: 'Instrucciones adicionales...' },
    ],
    permissions: [
      { label: 'Transferencia bancaria', enabled: true },
      { label: 'Confirmación manual por admin', enabled: true },
      { label: 'Acreditación 24-48h hábiles', enabled: true },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: true },
      { id: 'renewal', label: 'Renovaciones', enabled: true },
      { id: 'annual_license', label: 'Licencia anual', enabled: true },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: false },
      { id: 'premium_domain', label: 'Dominio premium', enabled: false },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: false },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
  {
    id: 'manual_payment',
    name: 'Pago Manual',
    icon: 'ri-file-list-3-line',
    logoColor: 'bg-amber-50 text-amber-600',
    category: 'manual',
    status: 'active',
    enabled: true,
    isDefault: false,
    testMode: false,
    order: 6,
    lastSync: null,
    lastTest: null,
    webhookStatus: null,
    commissionPercent: 0,
    commissionFixed: 0,
    descriptor: '',
    receiverName: '—',
    receiverContact: '—',
    manualInstructions: 'Subí tu comprobante de pago desde el panel de administración.\n\nEl equipo de RadioFrecuenciaWeb validará el pago dentro de las 24 horas hábiles.\n\nSerás notificado por email cuando el pago sea confirmado.',
    config: [
      { key: 'instructions', label: 'Instrucciones', value: 'Subí tu comprobante y esperá la validación. Te notificaremos por email cuando el pago sea confirmado.', type: 'textarea', placeholder: 'Instrucciones para el pagador...' },
    ],
    permissions: [
      { label: 'Pago manual con comprobante', enabled: true },
      { label: 'Validación por equipo RFW', enabled: true },
      { label: 'Notificación por email', enabled: true },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: true },
      { id: 'renewal', label: 'Renovaciones', enabled: true },
      { id: 'annual_license', label: 'Licencia anual', enabled: false },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: false },
      { id: 'premium_domain', label: 'Dominio premium', enabled: false },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: false },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
  {
    id: 'cash',
    name: 'Efectivo',
    icon: 'ri-money-dollar-circle-line',
    logoColor: 'bg-rose-50 text-rose-500',
    category: 'manual',
    status: 'active',
    enabled: false,
    isDefault: false,
    testMode: false,
    order: 7,
    lastSync: null,
    lastTest: null,
    webhookStatus: null,
    commissionPercent: 0,
    commissionFixed: 0,
    descriptor: '',
    receiverName: 'Cobro presencial',
    receiverContact: 'Oficina Central — Av. Corrientes 1234, CABA',
    manualInstructions: '1. Acercate a nuestra oficina en Av. Corrientes 1234, CABA.\n2. Horario: Lunes a Viernes de 9:00 a 18:00.\n3. Presentá el número de factura o contrato.\n4. Se aceptan pesos argentinos y dólares.\n5. Recibirás un recibo firmado al momento.',
    config: [
      { key: 'location', label: 'Dirección de cobro', value: 'Av. Corrientes 1234, CABA', type: 'text', placeholder: 'Dirección...' },
      { key: 'schedule', label: 'Horario', value: 'Lunes a Viernes 9:00-18:00', type: 'text', placeholder: 'Horario de atención...' },
      { key: 'instructions', label: 'Instrucciones', value: 'Acercate a la oficina con el número de factura. Horario: L-V 9:00-18:00.', type: 'textarea', placeholder: 'Instrucciones...' },
    ],
    permissions: [
      { label: 'Cobro presencial', enabled: true },
      { label: 'Solo disponible como mock', enabled: true },
    ],
    usageFunctions: [
      { id: 'new_radio', label: 'Nuevas radios', enabled: false },
      { id: 'renewal', label: 'Renovaciones', enabled: false },
      { id: 'annual_license', label: 'Licencia anual', enabled: false },
      { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: false },
      { id: 'premium_domain', label: 'Dominio premium', enabled: false },
      { id: 'plan_upgrade', label: 'Upgrade plan', enabled: false },
      { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
    ],
  },
];

export const defaultAnnualLicenseConfig: AnnualLicenseConfig = {
  enabled: true,
  amount: 49.95,
  periodicity: 'annual',
  description: 'Licencia anual de acceso a RadioFrecuenciaWeb.',
  includes: [
    'Actualizaciones',
    'Nuevas funciones',
    'Soporte plataforma',
    'Mantenimiento sistema',
    'Acceso continuo',
  ],
  stats: {
    payingRadios: 847,
    expiringThisMonth: 32,
    projectedRevenue: 42307.65,
  },
};